import React, { useState, useEffect } from "react";
import { dashboard, downloadExcel, exportData } from "../../lib/api";
import { KPICard } from "../shared/KPICard";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  DollarSign,
  TrendingUp,
  Wallet,
  Receipt,
  AlertTriangle,
  Download,
  RefreshCw,
  Loader2,
  Calendar,
} from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#ea580c", "#0f172a", "#059669", "#eab308", "#64748b"];

// Data padrão de início: 01/05/2025
const getDefaultStartDate = () => "2025-05-01";

// Data padrão de fim: data de hoje
const getDefaultEndDate = () => new Date().toISOString().split('T')[0];

export function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataInicio, setDataInicio] = useState(getDefaultStartDate());
  const [dataFim, setDataFim] = useState(getDefaultEndDate());

  const fetchData = async () => {
    try {
      const response = await dashboard.financeiro(dataInicio, dataFim);
      setData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleApplyFilter = () => {
    setLoading(true);
    fetchData();
  };

  const handleExport = async () => {
    try {
      await downloadExcel(exportData.notasFiscais, "notas_fiscais.xlsx");
      toast.success("Relatório exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar relatório");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Dashboard Financeiro
          </h1>
          <p className="text-muted-foreground">
            Visão geral da saúde financeira do negócio
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex items-center gap-4 flex-wrap bg-slate-50 p-4 rounded-lg border">
        <Calendar className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-medium">Período:</span>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground">De:</Label>
          <Input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="w-[160px]"
            data-testid="dashboard-data-inicio"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground">Até:</Label>
          <Input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="w-[160px]"
            data-testid="dashboard-data-fim"
          />
        </div>
        <Button onClick={handleApplyFilter} data-testid="dashboard-filter-btn">
          Aplicar Período
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Faturamento Bruto"
          value={data?.faturamento_mes || 0}
          icon={DollarSign}
          format="currency"
        />
        <KPICard
          title="Impostos Pagos"
          value={data?.impostos_pagos || 0}
          icon={Receipt}
          format="currency"
        />
        <KPICard
          title="Faturamento Líquido"
          value={data?.faturamento_liquido || 0}
          icon={DollarSign}
          format="currency"
        />
        <KPICard
          title="Recebimentos no Período"
          value={data?.recebimentos_mes || 0}
          icon={Wallet}
          format="currency"
        />
        <KPICard
          title="Valores a Receber"
          value={data?.valores_a_receber || 0}
          icon={AlertTriangle}
          format="currency"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          title="Margem Média"
          value={data?.margem_media || 0}
          icon={TrendingUp}
          format="percent"
        />
        <KPICard
          title="NFs Emitidas"
          value={data?.nfs_emitidas || 0}
          icon={Receipt}
          format="number"
        />
        <KPICard
          title="Saldo a Receber"
          value={data?.saldo_a_receber || 0}
          icon={TrendingUp}
          format="currency"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Receita por Período</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data?.receita_por_periodo || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: "4px", border: "1px solid #e2e8f0" }}
                />
                <Bar dataKey="valor" fill="#ea580c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Cash Flow Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Fluxo de Caixa</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data?.fluxo_caixa || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: "4px", border: "1px solid #e2e8f0" }}
                />
                <Line
                  type="monotone"
                  dataKey="entradas"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={{ fill: "#059669" }}
                  name="Entradas"
                />
                <Line
                  type="monotone"
                  dataKey="saidas"
                  stroke="#dc2626"
                  strokeWidth={2}
                  dot={{ fill: "#dc2626" }}
                  name="Saídas"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Clients */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Top Clientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(data?.top_clientes || []).map((cliente, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    >
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium truncate max-w-[120px]">
                      {cliente.nome}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">
                    {formatCurrency(cliente.valor)}
                  </span>
                </div>
              ))}
              {(!data?.top_clientes || data.top_clientes.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhum cliente encontrado
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Top Produtos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(data?.top_produtos || []).map((produto, index) => (
                <div key={index} className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    >
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium truncate" title={produto.nome}>
                      {produto.nome}
                    </span>
                  </div>
                  <span className="text-sm font-semibold flex-shrink-0">
                    {formatCurrency(produto.valor)}
                  </span>
                </div>
              ))}
              {(!data?.top_produtos || data.top_produtos.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhum produto encontrado
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
